import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import SnapshotSelector from '../components/SnapshotSelector';
import RankingTable from '../components/RankingTable';
import ComparisonView from '../components/ComparisonView';
import {
  apiService,
  RankingSnapshot,
  RankingSnapshotStats,
  RankingComparison,
  RankingComparisonRequest,
  RankingWeightsConfig
} from '../services/api';

// Helper: convert ISO timestamp to URL-friendly format
// "2026-01-16T11:06:02+00:00" → "2026-01-16T11-06-02"
const toUrlFriendly = (timestamp: string): string => {
  // Remove timezone and replace : with - in time part
  return timestamp
    .replace(/\+00:00$/, '')
    .replace(/Z$/, '')
    .replace(/(\d{2}):(\d{2}):(\d{2})/, '$1-$2-$3');
};

// Helper: convert URL-friendly format back to ISO timestamp
// "2026-01-16T11-06-02" → "2026-01-16T11:06:02+00:00"
const fromUrlFriendly = (urlParam: string): string => {
  // Replace - with : in time part (after T)
  const tIndex = urlParam.indexOf('T');
  if (tIndex === -1) return urlParam;

  const datePart = urlParam.substring(0, tIndex);
  const timePart = urlParam.substring(tIndex + 1).replace(/-/g, ':');
  // Adicionar timezone UTC para corresponder ao formato da API
  return `${datePart}T${timePart}+00:00`;
};

const RankingAnalysisPage: React.FC = () => {
  // URL params for persistence
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial values from URL (convert from URL-friendly format)
  const snapshotFromUrl = searchParams.get('snapshot');
  const snapshot2FromUrl = searchParams.get('snapshot2');
  const modeFromUrl = searchParams.get('mode') as 'single' | 'compare' | null;

  // Convert URL params to ISO format for internal use
  const initialSnapshot1 = snapshotFromUrl ? fromUrlFriendly(snapshotFromUrl) : null;
  const initialSnapshot2 = snapshot2FromUrl ? fromUrlFriendly(snapshot2FromUrl) : null;

  // State for snapshots (initialized from URL)
  const [selectedSnapshot1, setSelectedSnapshot1State] = useState<string | null>(initialSnapshot1);
  const [selectedSnapshot2, setSelectedSnapshot2State] = useState<string | null>(initialSnapshot2);

  // Wrapper to sync snapshot1 with URL (using URL-friendly format)
  const setSelectedSnapshot1 = useCallback((snapshot: string | null) => {
    setSelectedSnapshot1State(snapshot);
    const newParams = new URLSearchParams(searchParams);
    if (snapshot) {
      newParams.set('snapshot', toUrlFriendly(snapshot));
    } else {
      newParams.delete('snapshot');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Wrapper to sync snapshot2 with URL (using URL-friendly format)
  const setSelectedSnapshot2 = useCallback((snapshot: string | null) => {
    setSelectedSnapshot2State(snapshot);
    const newParams = new URLSearchParams(searchParams);
    if (snapshot) {
      newParams.set('snapshot2', toUrlFriendly(snapshot));
    } else {
      newParams.delete('snapshot2');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // State for single snapshot view
  const [rankings, setRankings] = useState<RankingSnapshot[]>([]);
  const [stats, setStats] = useState<RankingSnapshotStats | null>(null);
  const [rankingsLoading, setRankingsLoading] = useState(false);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const pageSize = 100;

  // State for sorting
  const [orderBy, setOrderBy] = useState<'position_asc' | 'position_desc' | 'score_asc' | 'score_desc' | 'penalty_asc' | 'penalty_desc' | 'last_sale_asc' | 'last_sale_desc' | 'expected_hours_asc' | 'expected_hours_desc'>('position_asc');

  // State for comparison view
  const [comparisons, setComparisons] = useState<RankingComparison[]>([]);
  const [comparisonsLoading, setComparisonsLoading] = useState(false);
  const [comparisonSort, setComparisonSort] = useState<RankingComparisonRequest['sort_by']>('position2');

  // State for UI (mode initialized from URL)
  const [mode, setModeState] = useState<'single' | 'compare'>(modeFromUrl === 'compare' ? 'compare' : 'single');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFullDetails, setShowFullDetails] = useState(true);

  // State for snapshot validation (evitar carregar snapshot inexistente)
  const [snapshot1Validated, setSnapshot1Validated] = useState(false);
  const [snapshot2Validated, setSnapshot2Validated] = useState(false);

  // Ranking weights (loaded from API)
  const [rankingWeights, setRankingWeights] = useState<RankingWeightsConfig['weights'] | null>(null);

  // Wrapper to sync mode with URL
  const setMode = useCallback((newMode: 'single' | 'compare') => {
    setModeState(newMode);
    const newParams = new URLSearchParams(searchParams);
    if (newMode === 'compare') {
      newParams.set('mode', 'compare');
    } else {
      newParams.delete('mode');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Load rankings for single snapshot
  const loadRankings = useCallback(async (timestamp: string) => {
    try {
      setRankingsLoading(true);

      const offset = (currentPage - 1) * pageSize;

      const [rankingResponse, statsResponse] = await Promise.all([
        apiService.getRankingSnapshot(timestamp, {
          limit: pageSize,
          offset: offset,
          search: searchTerm || undefined,
          order_by: orderBy
        }),
        apiService.getRankingSnapshotStats(timestamp)
      ]);

      setRankings(rankingResponse.results);
      setStats(statsResponse.stats);
      setTotalProducts(rankingResponse.total);
    } catch (error) {
      console.error('Error loading rankings:', error);
      alert('Erro ao carregar dados do ranking');
    } finally {
      setRankingsLoading(false);
    }
  }, [currentPage, searchTerm, orderBy]);

  // Load comparison between two snapshots
  const loadComparison = useCallback(async (timestamp1: string, timestamp2: string) => {
    try {
      setComparisonsLoading(true);
      
      // Garantir ordem temporal correta: timestamp1 deve ser mais antigo
      const date1 = new Date(timestamp1);
      const date2 = new Date(timestamp2);
      
      let orderedTimestamp1 = timestamp1;
      let orderedTimestamp2 = timestamp2;
      
      if (date1 > date2) {
        // Trocar se estiverem na ordem errada
        orderedTimestamp1 = timestamp2;
        orderedTimestamp2 = timestamp1;
        
        // Atualizar os estados para refletir a troca
        setSelectedSnapshot1(orderedTimestamp1);
        setSelectedSnapshot2(orderedTimestamp2);
      }
      
      const comparisonResponse = await apiService.compareRankingSnapshots({
        timestamp1: orderedTimestamp1,
        timestamp2: orderedTimestamp2,
        limit: 200,
        sort_by: comparisonSort
      });
      
      setComparisons(comparisonResponse.results);
    } catch (error: any) {
      console.error('Error loading comparison:', error);
      if (error.response?.status === 404) {
        // Se for 404, não há dados mas não é um erro crítico
        setComparisons([]);
        console.log('Nenhum dado de comparação encontrado');
      } else {
        alert('Erro ao carregar comparação. Por favor, tente novamente.');
      }
    } finally {
      setComparisonsLoading(false);
    }
  }, [comparisonSort, setSelectedSnapshot1, setSelectedSnapshot2]);

  // Load ranking weights config once
  useEffect(() => {
    apiService.getRankingWeights()
      .then(config => setRankingWeights(config.weights))
      .catch(() => {}); // fallback to defaults in components
  }, []);

  // Handle snapshot selection (only when validated)
  useEffect(() => {
    if (mode === 'single' && selectedSnapshot1 && snapshot1Validated) {
      loadRankings(selectedSnapshot1);
    }
  }, [selectedSnapshot1, mode, snapshot1Validated, loadRankings]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, orderBy]);

  useEffect(() => {
    if (mode === 'compare' && selectedSnapshot1 && selectedSnapshot2 && snapshot1Validated && snapshot2Validated) {
      loadComparison(selectedSnapshot1, selectedSnapshot2);
    }
  }, [selectedSnapshot1, selectedSnapshot2, mode, snapshot1Validated, snapshot2Validated, loadComparison]);

  // Handle mode change
  const handleModeChange = (newMode: 'single' | 'compare') => {
    setMode(newMode);
    if (newMode === 'single') {
      setSelectedSnapshot2(null);
      setComparisons([]);
    }
  };

  // Handle SKU click
  const handleSkuClick = (sku: string) => {
    window.open(`/products/${sku}`, '_blank');
  };

  // Format stats
  const formatStats = (stats: RankingSnapshotStats) => {
    return [
      { label: 'Total de Produtos', value: stats.total_products.toLocaleString() },
      { label: 'Score Médio', value: stats.avg_score.toFixed(4) },
      { label: 'Produtos com Penalizações', value: `${stats.products_with_penalties} (${((stats.products_with_penalties / stats.total_products) * 100).toFixed(1)}%)` },
      { label: 'Produtos com Boosts', value: `${stats.products_with_boosts} (${((stats.products_with_boosts / stats.total_products) * 100).toFixed(1)}%)` },
      { label: 'Mudanças de Preço', value: `${stats.products_with_price_changes} produtos` },
      { label: 'Receita Total 7d', value: `€${stats.total_revenue.toLocaleString()}` }
    ];
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>Análise de Rankings</h1>
            
            {/* Mode selector */}
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn ${mode === 'single' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handleModeChange('single')}
              >
                <i className="bi bi-list-ol me-1"></i>
                Snapshot Individual
              </button>
              <button
                type="button"
                className={`btn ${mode === 'compare' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handleModeChange('compare')}
              >
                <i className="bi bi-arrow-left-right me-1"></i>
                Comparar Snapshots
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Snapshot selectors */}
      <div className="row mb-4">
        <div className="col-md-6">
          <SnapshotSelector
            selectedSnapshot={selectedSnapshot1}
            onSnapshotChange={setSelectedSnapshot1}
            onValidated={setSnapshot1Validated}
            label={mode === 'compare' ? 'Snapshot 1 (Base - Mais Antigo)' : 'Snapshot'}
          />
        </div>
        {mode === 'compare' && (
          <div className="col-md-6">
            <SnapshotSelector
              selectedSnapshot={selectedSnapshot2}
              onSnapshotChange={setSelectedSnapshot2}
              onValidated={setSnapshot2Validated}
              label="Snapshot 2 (Atual - Mais Recente)"
            />
          </div>
        )}
      </div>

      {/* Single snapshot view */}
      {mode === 'single' && (
        <>
          {/* Controls */}
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="SKU, nome ou múltiplos SKUs (ex: P078224 C027955)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-sort-down"></i>
                </span>
                <select
                  className="form-select"
                  value={orderBy}
                  onChange={(e) => setOrderBy(e.target.value as any)}
                >
                  <option value="position_asc">Posição (1→N)</option>
                  <option value="position_desc">Posição (N→1)</option>
                  <option value="score_desc">Score (maior→menor)</option>
                  <option value="score_asc">Score (menor→maior)</option>
                  <option value="penalty_desc">Penalização (maior→menor)</option>
                  <option value="penalty_asc">Penalização (menor→maior)</option>
                  <option value="last_sale_desc">Última venda (recente→antiga)</option>
                  <option value="last_sale_asc">Última venda (antiga→recente)</option>
                  <option value="expected_hours_asc">Expectativa/hora (rápido→lento)</option>
                  <option value="expected_hours_desc">Expectativa/hora (lento→rápido)</option>
                </select>
              </div>
            </div>
            <div className="col-md-4 text-end">
              <div className="form-check form-switch d-inline-block">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showFullDetails"
                  checked={showFullDetails}
                  onChange={(e) => setShowFullDetails(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="showFullDetails">
                  Mostrar detalhes completos
                </label>
              </div>
            </div>
          </div>

          {/* Pagination Controls */}
          {totalProducts > pageSize && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    Mostrando {(currentPage - 1) * pageSize + 1} a {Math.min(currentPage * pageSize, totalProducts)} de {totalProducts} produtos
                  </div>
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <i className="bi bi-chevron-left"></i> Anterior
                    </button>
                    <button type="button" className="btn btn-outline-primary" disabled>
                      Página {currentPage} de {Math.ceil(totalProducts / pageSize)}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      disabled={currentPage >= Math.ceil(totalProducts / pageSize)}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Próxima <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats cards */}
          {stats && (
            <div className="row mb-4">
              {formatStats(stats).map((stat, index) => (
                <div key={index} className="col-md-2 col-sm-4 col-6 mb-2">
                  <div className="card text-center">
                    <div className="card-body py-2">
                      <h6 className="card-title small mb-1">{stat.label}</h6>
                      <p className="card-text fw-bold">{stat.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Rankings table */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">
                    Ranking {selectedSnapshot1 && `- ${new Date(selectedSnapshot1).toLocaleString('pt-PT', { timeZone: 'UTC' })}`}
                  </h5>
                </div>
                <div className="card-body p-0">
                  <RankingTable
                    rankings={rankings}
                    loading={rankingsLoading}
                    onSkuClick={handleSkuClick}
                    showFullDetails={showFullDetails}
                    snapshotTimestamp={selectedSnapshot1 || undefined}
                    weights={rankingWeights || undefined}
                  />
                </div>
                {showFullDetails && rankings.length > 0 && (
                  <div className="card-footer small text-muted">
                    <strong>Legenda:</strong> Score Componentes: {(() => {
                      const w = rankingWeights || { revenue: 0.55, stock: 0.15, category: 0.08, brand: 0.07, potential_sales: 0.03, quantity: 0.10, price: 0.02 };
                      return `Rec = Receita (${Math.round(w.revenue * 100)}%), Cat = Categoria (${Math.round(w.category * 100)}%), Mar = Marca (${Math.round(w.brand * 100)}%), Pot = Potencial (${Math.round(w.potential_sales * 100)}%), Qtd = Quantidade (${Math.round(w.quantity * 100)}%), Prc = Preço (${Math.round(w.price * 100)}%)`;
                    })()} |
                    DPD: H = Hoje, O = Ontem |
                    Score: Verde = Boost (+%), Vermelho = Penalização (-%)
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pagination Controls Bottom */}
          {totalProducts > pageSize && (
            <div className="row mt-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    Mostrando {(currentPage - 1) * pageSize + 1} a {Math.min(currentPage * pageSize, totalProducts)} de {totalProducts} produtos
                  </div>
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <i className="bi bi-chevron-left"></i> Anterior
                    </button>
                    <button type="button" className="btn btn-outline-primary" disabled>
                      Página {currentPage} de {Math.ceil(totalProducts / pageSize)}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      disabled={currentPage >= Math.ceil(totalProducts / pageSize)}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Próxima <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Comparison view */}
      {mode === 'compare' && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  Comparação de Rankings
                  {selectedSnapshot1 && selectedSnapshot2 && (
                    <span className="text-muted ms-2">
                      <i className="bi bi-clock-history me-1"></i>
                      {new Date(selectedSnapshot1).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' })}
                      <i className="bi bi-arrow-right mx-2"></i>
                      {new Date(selectedSnapshot2).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' })}
                    </span>
                  )}
                </h5>
              </div>
              <div className="card-body p-0">
                {selectedSnapshot1 && selectedSnapshot2 ? (
                  comparisons.length > 0 || comparisonsLoading ? (
                    <ComparisonView
                      comparisons={comparisons}
                      loading={comparisonsLoading}
                      onSkuClick={handleSkuClick}
                      onSortChange={setComparisonSort}
                      currentSort={comparisonSort}
                    />
                  ) : (
                    <div className="p-4 text-center text-muted">
                      <i className="bi bi-info-circle me-2"></i>
                      Nenhuma mudança de ranking encontrada entre os snapshots selecionados.
                    </div>
                  )
                ) : (
                  <div className="p-4 text-center text-muted">
                    Seleccione dois snapshots para comparar
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RankingAnalysisPage;